import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { PERIL_LABELS, type Declaration, type PerilId } from "../../lib/risks";

const PERIL_COLORS: Record<PerilId, string> = {
  inondation: "#2f6df6",
  secheresse: "#cf8b1e",
  mouvement: "#7a5195",
  tempete: "#5a6472",
  autre: "#a9b1bd",
};

/**
 * Every disaster declaration since 1982, one bar per year.
 *
 * A timeline rather than a total, because the shape is the whole message:
 * three floods clustered in 2016–2018 and nothing since is a different
 * commune from one declaring something every other year, and both come to the
 * same number.
 *
 * Empty years are kept in the data so the gaps are visible — a bar chart that
 * silently skips 1998–2015 draws a run of disasters that never happened.
 */
export function CatnatTimeline({
  declarations,
  peril,
}: {
  declarations: Declaration[];
  peril: PerilId | "all";
}) {
  const rows = peril === "all" ? declarations : declarations.filter((d) => d.peril === peril);

  const byYear = new Map<number, { n: number; perils: Set<PerilId> }>();
  for (const d of rows) {
    const entry = byYear.get(d.year) ?? { n: 0, perils: new Set<PerilId>() };
    entry.n += 1;
    entry.perils.add(d.peril);
    byYear.set(d.year, entry);
  }

  const last = new Date().getFullYear();
  const data: { year: number; n: number; color: string; what: string }[] = [];
  for (let year = 1982; year <= last; year++) {
    const entry = byYear.get(year);
    const perils = entry ? [...entry.perils] : [];
    data.push({
      year,
      n: entry?.n ?? 0,
      // Mixed years take the colour of the first peril declared; the tooltip
      // carries the full list, and colour here is a hint, not a key.
      color: perils.length ? PERIL_COLORS[perils[0]] : "#e3e7ed",
      what: perils.map((p) => PERIL_LABELS[p]).join(", "),
    });
  }

  return (
    <div className="h-24 w-full">
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 4, right: 6, bottom: 0, left: -14 }}>
          <XAxis
            dataKey="year"
            tick={{ fontSize: 9, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            interval={9}
          />
          <YAxis
            tick={{ fontSize: 9, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            width={22}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: "#00000008" }}
            contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e3e7ed" }}
            formatter={(value, _name, item) => [
              `${value} declared${item?.payload?.what ? ` · ${item.payload.what}` : ""}`,
              "",
            ]}
            labelFormatter={(year) => String(year)}
          />
          <Bar dataKey="n" isAnimationActive={false}>
            {data.map((d) => (
              <Cell key={d.year} fill={d.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Swatch legend, rendered by the panel rather than by recharts. */
export function PerilKey({ perils }: { perils: PerilId[] }) {
  if (perils.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
      {perils.map((p) => (
        <span key={p} className="flex items-center gap-1 text-[9px] text-ink-soft">
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ background: PERIL_COLORS[p] }}
          />
          {PERIL_LABELS[p]}
        </span>
      ))}
    </div>
  );
}
